/*
Notes:
  if the order is placed before 12:00 Eastern Time,
  then delivery date will be the next day, but next day couldn't be sunday or monday
  and can't be global block out dates(theme settings) and can't be product block out dates(coming from metafields)

  if the order is placed after 12:00 Eastern Time,
  then delivery date will be the day after tomorrow, but next day couldn't be sunday or monday
  and can't be global block out dates(theme settings) and can't be product block out dates(coming from metafields)

  if the order is placed before 12:00 Eastern Time,
  but the next day is sunday, then delivery date will be Tuesday
*/

import { defineElement, getEasternDate } from '~/utils';
import { formatDate, getMonthSlashDay, getWeekdayName, CSVToArray, isBlockedDate } from '~/utils';
import { getCutoffTime, getLongDate } from '~/utils/calendar-utils';

class NextDeliveryDate extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.arrivalDate = null;
    this.shippingDate = null;

    // All dates are in the format 'MM-DD-YYYY'
    // globalBlockOutDates is an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this._globalBlockOutDates = CSVToArray(window.Boarderie.blockoutDates.global);
    // productBlockOutDates and productSoldOutDates are an array of dates (e.g. ['01-01-2022', '01-02-2022'])
    this._productBlockOutDates = CSVToArray(window.Boarderie.blockoutDates.product.blockout);
    this._productSoldOutDates = CSVToArray(window.Boarderie.blockoutDates.product.soldout);
    // repeatingDisabledDates is an array of weekdays (e.g. ['Monday', 'Tuesday'])
    this._repeatingDisabledDates = CSVToArray(window.Boarderie.blockoutDates.repeatingDisabledDates);

    this._is_egift = this.hasAttribute('is-egift');
    this.longDateFormat = this.hasAttribute('long-date-format');
    this.onlyWeekName = this.hasAttribute('only-week-name');
  }

  static get observedAttributes() {
    return ['product-block-out-dates', 'product-sold-out-dates'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'product-block-out-dates') {
      this._productBlockOutDates = CSVToArray(newValue);

      if (this._is_egift) {
        this.renderInstantDelivery();
      } else {
        this.renderNextAvailableDelivery();
      }
    }

    if (name === 'product-sold-out-dates') {
      this._productSoldOutDates = CSVToArray(newValue);

      if (this._is_egift) {
        this.renderInstantDelivery();
      } else {
        this.renderNextAvailableDelivery();
      }
    }
  }

  connectedCallback() {
    if (this._is_egift) {
      this.renderInstantDelivery();
    } else {
      this.renderNextAvailableDelivery();
    }
  }

  renderNextAvailableDelivery() {
    const deliveryDate = this.calculateDeliveryDate(
      this._globalBlockOutDates,
      this._productBlockOutDates,
      this._productSoldOutDates,
      this._repeatingDisabledDates
    );

    if (this.longDateFormat) {
      const arrivalDateInLongFormat = getLongDate(deliveryDate);
      this.shadowRoot.innerHTML = `<div><span style="margin-right: 0.4rem"> - </span>${arrivalDateInLongFormat}</div>`;
    } else if (this.onlyWeekName) {
      const weekdayName = getWeekdayName(deliveryDate);
      const weekIndicator = this.getWeekIndicator(deliveryDate);
      this.shadowRoot.innerHTML = `<div style="display:inline-block;">${weekIndicator} ${weekdayName}</div>`;
    } else {
      const daySlashMonth = getMonthSlashDay(deliveryDate);
      const weekdayName = getWeekdayName(deliveryDate);
      const weekIndicator = this.getWeekIndicator(deliveryDate);
      this.shadowRoot.innerHTML = `<div>${weekIndicator} ${weekdayName} (${daySlashMonth})</div>`;
    }

    const arrivalDateISO = deliveryDate.toISOString();
    const formattedArrivalDate = formatDate({
      date: deliveryDate,
      separator: '/',
    });

    const shippingDate = new Date(deliveryDate);
    shippingDate.setDate(deliveryDate.getDate() - 1);
    const formattedShippingDate = formatDate({
      date: shippingDate,
      separator: '/',
    });

    // Set the arrival date and shipping date as properties
    this.arrivalDate = formattedArrivalDate;
    this.arrivalDateISO = arrivalDateISO;
    this.shippingDate = formattedShippingDate;

    const rightAwayInput = this.closest('.form-option')?.querySelector('input[data-delivery-date-checkbox-right-away]');
    if (rightAwayInput) {
      const currentAriaLabel = rightAwayInput.getAttribute('aria-label');
      if (currentAriaLabel) {
        rightAwayInput.setAttribute(
          'aria-label',
          currentAriaLabel.replace('[[ right_away_date ]]', formattedArrivalDate)
        );
      }
    }

    this.dispatchEvent(new CustomEvent('delivery-date-rendered', {
      detail: {
        arrivalDate: formattedArrivalDate,
        arrivalDateISO: arrivalDateISO,
        shippingDate: formattedShippingDate,
      }
    }));
  }

  calculateDeliveryDate(globalBlockOutDates, productBlockOutDates, productSoldOutDates, repeatingDisabledDates) {
    const cutoffTime = getCutoffTime();

    // Create a new Date object set to Eastern Time
    let { date: easternDate, hour } = getEasternDate();
    let deliveryDate = easternDate;

    // Determine if the order was placed before or after the cutoff time
    const isBeforeCutOffTime = hour < cutoffTime;

    const saturdayDayNumber = 6;
    const isSaturday = easternDate.getDay() === saturdayDayNumber;


    if (isBeforeCutOffTime) {
      // Order placed before cutoff time
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      // Order placed after cutoff time
      deliveryDate.setDate(deliveryDate.getDate() + 2);
    }

    // Find the next available date considering block out dates
    deliveryDate = this.getNextAvailableDate(
      deliveryDate,
      globalBlockOutDates,
      productBlockOutDates,
      productSoldOutDates,
      repeatingDisabledDates
    );

    return deliveryDate;
  }

  getNextAvailableDate(deliveryDate, globalBlockOutDates, productBlockOutDates, productSoldOutDates, repeatingDisabledDates) {
    const maxIterations = 30; // prevent infinite loop
    let iterations = 0;

    while (iterations <= maxIterations) {
      const { isBlocked, blockedConditions } = isBlockedDate(
        deliveryDate,
        globalBlockOutDates,
        productBlockOutDates,
        productSoldOutDates,
        repeatingDisabledDates
      );

      if (!isBlocked) {
        return deliveryDate;
      } else {
        console.log(`Date ${deliveryDate} is blocked due to: ${blockedConditions.join(', ')}`);
      }

      deliveryDate.setDate(deliveryDate.getDate() + 1);
      iterations++;
    }

    console.error('Could not find an available delivery date within 30 days.');
    return null;
  }

  getWeekIndicator(deliveryDate) {
    // Get the current date in Eastern Time
    const { date: easternDate } = getEasternDate();

    // Get the start of the current week (Sunday) in Eastern Time
    const startOfCurrentWeek = new Date(easternDate);
    startOfCurrentWeek.setDate(easternDate.getDate() - easternDate.getDay()); // Set to Sunday

    // Get the start of the next week (next Sunday) in Eastern Time
    const startOfNextWeek = new Date(startOfCurrentWeek);
    startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);

    // Check if the delivery date falls within this week or next week
    if (deliveryDate >= startOfCurrentWeek && deliveryDate < startOfNextWeek) {
      return 'This';
    } else if (
      deliveryDate >= startOfNextWeek &&
      deliveryDate < new Date(startOfNextWeek).setDate(startOfNextWeek.getDate() + 7)
    ) {
      return 'This';
    } else {
      return ''; // Default case if it's beyond next week
    }
  }

  renderInstantDelivery() {
    const { date } = getEasternDate();
    const todayInLongFormat = getLongDate(date);
    const arrivalDateISO = date.toISOString();
    const formattedToday = formatDate({
      date: date,
      separator: '/',
    });

    this.shippingDate = formattedToday;
    this.arrivalDate = formattedToday;
    this.arrivalDateISO = arrivalDateISO;

    this.shadowRoot.innerHTML = `<div><span style="margin-right: 0.4rem"> - </span>${todayInLongFormat}</div>`;

    const rightAwayInput = this.closest('.form-option')?.querySelector('input[data-delivery-date-checkbox-right-away]');
    if (rightAwayInput) {
      const currentAriaLabel = rightAwayInput.getAttribute('aria-label');
      if (currentAriaLabel) {
        rightAwayInput.setAttribute('aria-label', currentAriaLabel.replace('[[ right_away_date ]]', formattedToday));
      }
    }
  }
}

defineElement('next-delivery-date', NextDeliveryDate);
